import React from 'react';
import { Link } from 'react-router-dom';
import { FullPage } from '../../layouts';
import { Container, Logo } from '../../components';
import { routes } from '../../routes/Routes';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => (
  <FullPage>
    <Container className="privacy-policy-container">
      <div className="privacy-policy-header">
        <Logo />
      </div>
      <Link to={routes.login} className="privacy-policy-back-link">
        ← Go back to login screen
      </Link>

      <div className="privacy-policy-content">
        <h1>Privacy Policy – DriveX Admin Panel</h1>

        <p><strong>Last updated:</strong> 26.06.2026</p>

        <p>
          This Privacy Policy describes how <strong>DriveX Technologies OÜ</strong> ("
          <strong>DriveX</strong>", "<strong>we</strong>", "<strong>us</strong>", or "
          <strong>our</strong>") processes personal data in connection with the use of the{' '}
          <strong>DriveX Admin Panel</strong> (the "Platform").
        </p>

        <p>
          This policy does not govern the collection of data through other DriveX products
          (such as SmartScan), which are covered by their respective privacy policies.
        </p>

        <p>
          Data displayed in the Admin Panel originates from DriveX's SmartScan application and other DriveX services.
          For information about how that data is collected, processed, used, and your rights regarding it,
          please see our{' '}
          <a
            href="https://api.drivex.io/languages/6152809352bb4500015a68f2/termsAndConditions"
            target="_blank"
            rel="noopener noreferrer"
          >
            SmartScan Terms and Conditions
          </a>{' '}
          and{' '}
          <a
            href="https://api.drivex.io/languages/6152809352bb4500015a68f2/gdprPrivacyPolicy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          .
        </p>

        <hr />

        <section>
          <h2>1. Controller Information</h2>
          <p><strong>Company name:</strong> DriveX Technologies OÜ</p>
          <p>
            <strong>Registered address:</strong> Staapli tn 3-92, Põhja-Tallinna linnaosa,
            10415 Tallinn, Harju maakond
          </p>
          <p>
            <strong>Contact:</strong>{' '}
            <a href="mailto:info@drivex.io">info@drivex.io</a>
          </p>
        </section>

        <hr />

        <section>
          <h2>2. Scope of This Policy</h2>
          <p>This Privacy Policy applies to:</p>
          <ul>
            <li>Authorized users of the DriveX Admin Panel</li>
            <li>Data processed for authentication, access control, and administration purposes</li>
          </ul>
        </section>

        <hr />

        <section>
          <h2>3. Roles Under Data Protection Law</h2>
          <p>Depending on the context, DriveX acts as:</p>

          <h3>Data Controller</h3>
          <p>For user authentication data and platform access management.</p>

          <h3>Data Processor</h3>
          <p>
            When displaying or processing customer-provided data within the Admin Panel
            on behalf of the customer.
          </p>
        </section>

        <hr />

        <section>
          <h2>4. Data We Process</h2>

          <h3>4.1 Account and Authentication Data</h3>
          <p>To provide secure access to the Admin Panel, we process:</p>
          <ul>
            <li>Business email address</li>
            <li>Authentication credentials and session data (e.g. access and refresh tokens)</li>
          </ul>

          <p>
            Email addresses must belong to the customer's organization (e.g. name@company.com).
            Personal email addresses are not accepted (e.g. name@gmail.com, or other general
            email providers).
          </p>

          <p>
            Email addresses may contain first and last names depending on the customer's
            internal email structure. This is determined by the customer and not required
            by DriveX.
          </p>

          <h3>4.2 Data Displayed in the Admin Panel</h3>
          <p>
            The Admin Panel may display data submitted by the customer through other DriveX
            services, such as:
          </p>
          <ul>
            <li>Vehicle information (e.g. VIN, license plate)</li>
            <li>Vehicle images</li>
            <li>External system ID-s</li>
          </ul>

          <p>
            This data is provided by the customer through DriveX services. Within the Admin
            Panel, DriveX acts as a processor, displaying this data for customer access and
            management purposes. For information about how DriveX collects and uses this data
            as a controller, see our SmartScan Privacy Policy.
          </p>
        </section>

        <hr />

        <section>
          <h2>5. Content Sharing Features</h2>
          <p>The Admin Panel allows customers to share selected content in two ways:</p>

          <h3>5.1 Sharing with Other DriveX Customers</h3>
          <p>
            Content may be shared with other authenticated DriveX customers through controlled
            access mechanisms.
          </p>

          <h3>5.2 Public Sharing Links</h3>
          <p>
            Customers may generate publicly accessible links for selected content.
          </p>

          <p>When using public sharing:</p>
          <ul>
            <li>The customer acts as the <strong>data controller</strong></li>
            <li>
              DriveX acts solely as a <strong>data processor</strong>, providing the technical
              functionality
            </li>
            <li>
              Anyone with access to the link may view the shared content until access is revoked
            </li>
          </ul>

          <p>DriveX does not index, promote, or distribute public links.</p>

          <p>The customer is solely responsible for:</p>
          <ul>
            <li>What content is shared</li>
            <li>Ensuring a lawful basis for sharing</li>
            <li>Managing access to and revocation of shared links</li>
          </ul>
        </section>

        <hr />

        <section>
          <h2>6. Cookies and Similar Technologies</h2>
          <p>
            The Admin Panel uses <strong>strictly necessary cookies</strong> required for
            secure operation, including:
          </p>
          <ul>
            <li>Authentication and session management</li>
            <li>Security and access control</li>
            <li>Technical routing and performance</li>
          </ul>

          <table>
            <thead>
              <tr>
                <th>Cookie name</th>
                <th>Purpose</th>
                <th>Type</th>
                <th>Expiry</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>__Secure-DriveX-Auth-Token-*</td>
                <td>Authenticate users / maintain the active session</td>
                <td>Session</td>
                <td>Session (cleared when browser closes)</td>
              </tr>
              <tr>
                <td>__Secure-DriveX-Refresh-Token-*</td>
                <td>Maintain secure login sessions (silent renewal)</td>
                <td>Persistent</td>
                <td>3 days from issue</td>
              </tr>
            </tbody>
          </table>

          <p>
            It is possible to block or delete cookies through browser settings;
            however, because these cookies are strictly necessary for authentication and session management,
            disabling them will prevent you from logging in and using the Admin Panel.
            No cookies are used for analytics, advertising, or tracking purposes.
            As only strictly necessary cookies are used,{' '}
            <strong>user consent is not required</strong>.
          </p>
        </section>

        <hr />

        <section>
          <h2>7. Legal Basis for Processing</h2>
          <p>Personal data is processed based on:</p>
          <ul>
            <li>
              <strong>Article 6(1)(b) GDPR</strong> – performance of a contract
            </li>
            <li>
              <strong>Article 6(1)(f) GDPR</strong> – legitimate interest in ensuring
              platform security and reliability
            </li>
          </ul>
        </section>

        <hr />

        <section>
          <h2>8. Data Retention</h2>
          <p>Data is retained only for as long as necessary to:</p>
          <ul>
            <li>Provide access to the Admin Panel</li>
            <li>Fulfill contractual obligations</li>
            <li>Meet applicable legal or regulatory requirements</li>
          </ul>
          <p>
            Shared content remains accessible until removed or access is revoked by the customer.
          </p>
        </section>

        <hr />

        <section>
          <h2>9. Data Recipients</h2>
          <p>Access to data may be granted to:</p>
          <ul>
            <li>Authorized personnel of DriveX</li>
            <li>
              Trusted subprocessors providing infrastructure or hosting services, under
              contractual confidentiality obligations
            </li>
          </ul>
          <p>Data is not sold or used for marketing purposes.</p>
        </section>

        <hr />

        <section>
          <h2>10. Data Security</h2>
          <p>
            DriveX implements appropriate technical and organizational measures to protect
            data, including:
          </p>
          <ul>
            <li>Access control mechanisms</li>
            <li>Secure authentication processes</li>
            <li>Encrypted communications</li>
          </ul>
        </section>

        <hr />

        <section>
          <h2>11. Data Subject Rights</h2>
          <p>
            Where applicable under data protection law, individuals may have the right to:
          </p>
          <ul>
            <li>Access their personal data</li>
            <li>Request correction or deletion</li>
            <li>Restrict or object to processing</li>
          </ul>
          <p>Requests can be submitted using the contact details below.</p>
        </section>

        <hr />

        <section>
          <h2>12. Contact</h2>
          <p>
            For questions regarding this Privacy Policy or the processing of personal data,
            please contact:
          </p>
          <p>
            <strong>DriveX Technologies OÜ</strong>
            <br />
            <a href="mailto:info@drivex.io">info@drivex.io</a>
          </p>
        </section>
      </div>
    </Container>
  </FullPage>
);

export default PrivacyPolicy;
